import { Component, CSSProperties } from 'react'
import * as cn from 'classnames'
import * as styles from 'components/features/pfm/Highlights/Highlights.styl'
import { Insight as InsightType } from 'interfaces/insights'
import Carousel from 'components/molecules/Carousel'
import { insightShownActionCreator } from 'actions/insightsActions'
import { createFakeInsight } from 'helpers/insightHelpers'
import { NO_HIGHLIGHTS_TEXT } from 'constants/opportunities'
import { ViewportMatchName } from 'interfaces/viewport'
import Insight from 'components/features/insights/Insight/Insight'
import { WithMobileProps } from 'hocs/withMobile/withMobile'
import withPerformanceMetrics from 'hocs/withPerformanceMetrics/withPerformanceMetrics'
import ModuleWrapper2020 from 'components/features/pfm/ModuleWrapper2020/ModuleWrapper2020'

const EMPTY_STATE = createFakeInsight<InsightType>({
  placement: 'dashboard_opportunities',
  type: 'zero_state',
  title: 'You’re all set!',
  text: NO_HIGHLIGHTS_TEXT,
  tags: [],
})

const ONE_HIGHLIGHT_VIEWPORTS: ViewportMatchName[] = ['w320', 'w375', 'w415']

const WIDTH_BY_VIEWPORT: Record<ViewportMatchName, string> = {
  w320: '95%',
  w375: '85%',
  w415: '85%',
  w768: '47%',
  w1024: '47%',
  w1280: '47%',
  w1440: '33.333%',
  w1920: '25%',
  'not-defined': '33.333%',
}

const MIN_SLIDE_COUNT_BY_VIEWPORT: Record<ViewportMatchName, number> = {
  w320: 1,
  w375: 1,
  w415: 1,
  w768: 2,
  w1024: 2,
  w1280: 2,
  w1440: 3,
  w1920: 4,
  'not-defined': 3,
}

const REFINE_INDEX = (nextActiveIndex: number, activeIndex: number, isSwipe: boolean) => {
  if (!isSwipe) {
    // Index is changed, because user clicked on the button
    return nextActiveIndex
  }
  // Index is changed at most 1 position, because user performed swipe
  return Math.max(Math.min(nextActiveIndex, activeIndex + 1), activeIndex - 1)
}

const NO_REFINE_INDEX = (nextActiveIndex: number) => nextActiveIndex

export type HighlightsStateProps = {
  viewport: ViewportMatchName | null
  insights: InsightType[]
  dismissedInsights: InsightType[]
}

export type HighlightsDispatchProps = {
  onInsightShown: typeof insightShownActionCreator
  undismiss: (dismissedInsights: InsightType[]) => void
}

type HighlightsProps =
  & HighlightsStateProps
  & HighlightsDispatchProps
  & WithMobileProps

type HighlightsState = {
  carouselActiveIndex: number
  insightCount: number
}

class Highlights extends Component<HighlightsProps, HighlightsState> {
  static getDerivedStateFromProps (nextProps: HighlightsProps, prevState: HighlightsState) {
    if (nextProps.isMobile || nextProps.insights.length === prevState.insightCount) {
      return prevState
    }

    const insightCount = nextProps.insights.length
    const carouselActiveIndex = Math.min(
      prevState.carouselActiveIndex,
      insightCount - 1,
    )
    return { carouselActiveIndex, insightCount }
  }

  state = {
    insightCount: this.props.insights.length,
    carouselActiveIndex: 0,
  }

  private getTitle () {
    const { placement } = this.props.insights[0] ?? {}
    switch (placement) {
      case 'future_opportunities': {
        return 'Insights'
      }

      case 'coaching_carousel': {
        return 'Recent Coach Reviews'
      }
    }
    return 'Highlights'
  }

  render () {
    const { insights } = this.props
    const hasHighlights = insights.length > 0
    const highlightsModule = hasHighlights ? (
      <ModuleWrapper2020
        title={ this.getTitle() }
        card={ false }
        noPadding
      >
        <div className={ styles.highlights }>
          <div className={ styles.carouselWrapper }>
            { this.renderCarousel() }
          </div>
        </div>
      </ModuleWrapper2020>
    ) : null


    // Render placeholder to facilitate collapsing animation
    return (
      <>
        { highlightsModule }
        <div className={ cn(styles.emptyPlaceholder, { [styles.collapsed]: !hasHighlights }) } />
      </>
    )
  }

  renderCarousel () {
    const { insights, viewport, isMobile } = this.props
    const currentViewport = viewport || 'w1920'

    const minSlideCount = MIN_SLIDE_COUNT_BY_VIEWPORT[currentViewport]
    const width = insights.length > 1 || !ONE_HIGHLIGHT_VIEWPORTS.includes(currentViewport)
      ? WIDTH_BY_VIEWPORT[currentViewport]
      : '100%'

    const slideStyle: CSSProperties = { width, overflow: 'visible' }
    const containerStyle: CSSProperties = { overflowX: 'visible' }
    const highlights = insights.length ? insights : [EMPTY_STATE]

    return (
      <Carousel
        isFinite={ !isMobile }
        refineIndex={ isMobile ? REFINE_INDEX : NO_REFINE_INDEX }
        minSlideCount={ minSlideCount }
        activeIndex={ this.state.carouselActiveIndex }
        className={ styles.carousel }
        slideStyle={ slideStyle }
        containerStyle={ containerStyle }
        onIndexChange={ this.handleIndexChange }
      >
        { highlights.map((insight) => this.renderInsightWrapper(insight)) }
      </Carousel>
    )
  }

  renderInsightWrapper (insight: InsightType) {
    const className = cn(styles.insightWrapper, {
      [styles.onlyChild]: this.props.insights.length <= 1,
    })

    return (
      <div className={ className } key={ insight.id }>
        { this.renderInsight(insight) }
      </div>
    )
  }

  renderInsight (insight: InsightType) {
    const { onInsightShown } = this.props

    return (
      <Insight
        insight={ insight }
        onShown={ onInsightShown }
      />
    )
  }

  private handleIndexChange = (carouselActiveIndex: number) => {
    this.setState({ carouselActiveIndex })
  }
}

const HighlightsWithMetrics = withPerformanceMetrics(Highlights, 'highlights')

export default HighlightsWithMetrics
